import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  X,
  FileText,
  Sparkles,
  Share,
  Check,
  Printer,
  Image as ImageIcon,
  Terminal,
  Database,
  CloudLightning,
  MonitorCheck,
  AlertTriangle,
  Download,
} from "lucide-react";
import { AppMode, GeneratedImageAsset } from "../types";

interface ExportAttachment {
  id?: string;
  name?: string;
  type?: string;
  content?: string;
  base64?: string;
  mimeType?: string;
}

interface ExportCommand {
  type?: string;
  title?: string;
  code?: string;
}

interface ExportReport {
  id?: string;
  title?: string;
  text?: string;
}

interface ExportMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: string;
  imageUrl?: string;
  imageAsset?: GeneratedImageAsset;
  attachments?: ExportAttachment[];
  commands?: ExportCommand[];
  reports?: ExportReport[];
  isModeChangeIndicator?: boolean;
  driveEvidenceSummary?: any;
  modelRoute?: any;
  webResearch?: any;
}

interface AIExporterModalProps {
  isOpen: boolean;
  onClose: () => void;
  messages: ExportMessage[];
  currentMode: AppMode;
  onUploadFile: (name: string, content: string, mimeType?: string) => Promise<void>;
  driveSyncEnabled: boolean;
  globalTheme?: "rodgerspc" | "light" | "dark";
}

type SaveStatus = "idle" | "saving" | "saved" | "error";
type ExportExt = "txt" | "md" | "doc" | "html" | "json";
type RichExportMode = "clean" | "evidence";

type NamedAsset = {
  label: string;
  src: string;
  mimeType: string;
  fileName: string;
};

const OPERATOR_EMAIL = "joshua@rodgerspcservices.com";
const BUSINESS_NAME = "RodgersPC Computer Repair & Sales";
const SHOP_ADDRESS = "511 Lake Rd Suite 104, Belton, TX 76513";

function modeLabel(mode: AppMode | string): string {
  const raw = String(mode || "");
  const normalized = raw.toLowerCase().replace(/\s+/g, "_");

  const labels: Record<string, string> = {
    tech_assistant: "Bench Tech",
    tech_bench: "Bench Tech",
    technician_assistant: "Bench Tech",
    bench_tech: "Bench Tech",
    blog_engine: "Blog Engine",
    personal_vault: "Personal Vault",
    inventory_scout: "Inventory Scout",
    lead_responder: "Lead Responder",
    seo_listings: "SEO & Listings",
    web_creative: "Web & Creative Hub",
    front_desk: "Front Desk",
  };

  return labels[normalized] || raw
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function aiSenderLabelForMode(mode: AppMode | string): string {
  return `AI — ${modeLabel(mode).toUpperCase()}`;
}

function userSenderLabel(): string {
  return "USER — JOSHUA";
}


function cleanFilePart(value: string): string {
  return value
    .replace(/[\\/:*?"<>|]/g, "")
    .replace(/\s+/g, " ")
    .replace(/\s+[–-]\s+[–-]\s+/g, " – ")
    .trim();
}

function todayFileDate(): string {
  return new Date()
    .toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    })
    .replace(/\//g, "-");
}

function todayIsoFileDate(): string {
  const now = new Date();
  const yyyy = String(now.getFullYear());
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function isBlogEngineMode(mode: AppMode | string): boolean {
  const normalized = String(mode || "").toLowerCase().replace(/\s+/g, "_");
  return normalized === "blog_engine" || normalized === "blog" || normalized === "blogengine";
}

function escapeHtml(value: string): string {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function escapeAttr(value: string): string {
  return escapeHtml(value).replace(/`/g, "&#096;");
}

function textFromMessages(messages: ExportMessage[]): string {
  return messages.map((m) => m.content || "").join("\n");
}

function normalizedExportText(value: string): string {
  return String(value || "")
    .toLowerCase()
    .replace(/[’‘]/g, "'")
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function isInternalTestOrMixedLookup(messages: ExportMessage[]): boolean {
  const text = normalizedExportText(textFromMessages(messages));

  const hasModeIdentityAsk =
    text.includes("before answering") &&
    text.includes("which rodgerspc mode") &&
    text.includes("tools context");

  const hasWarranty = text.includes("warranty") || text.includes("service plans");
  const hasRamOrRetail =
    text.includes("ram") ||
    text.includes("corsair") ||
    text.includes("vengeance") ||
    text.includes("amazon") ||
    text.includes("price") ||
    text.includes("delivery");

  const hasTestingLanguage =
    text.includes("i m testing") ||
    text.includes("im testing") ||
    text.includes("testing whether") ||
    text.includes("test prompt") ||
    text.includes("web off") ||
    text.includes("web on");

  return (hasWarranty && hasRamOrRetail && (hasModeIdentityAsk || hasTestingLanguage)) ||
    (hasModeIdentityAsk && hasRamOrRetail);
}

function isLikelyProductOrSystemName(candidate: string): boolean {
  const lower = normalizedExportText(candidate);
  if (!lower) return true;

  const productSignals = [
    "samsung",
    "990 pro",
    "980 pro",
    "970 evo",
    "mz v9p2t0b",
    "nvme",
    "m.2",
    "ssd",
    "2tb",
    "1tb",
    "4tb",
    "crucial",
    "kingston",
    "western digital",
    "sandisk",
    "seagate",
    "corsair",
    "vengeance",
    "lpx",
    "rgb pro",
    "ddr4",
    "ddr5",
    "ram",
    "memory",
    "amazon",
    "ebay",
    "front desk",
    "bench tech",
    "tech assistant",
    "blog engine",
    "inventory scout",
    "lead responder",
    "seo listings",
    "web creative",
    "personal vault",
  ];

  return productSignals.some((signal) => lower.includes(signal));
}

function isBadSubjectFallback(candidate: string): boolean {
  const lower = candidate.trim().toLowerCase();
  return (
    !lower ||
    lower === "tech assistant" ||
    lower === "bench tech" ||
    lower === "tech bench" ||
    lower === "front desk" ||
    lower === "lead responder" ||
    lower === "inventory scout" ||
    lower === "blog engine" ||
    lower === "seo listings" ||
    lower === "seo & listings" ||
    lower === "web creative" ||
    lower === "web & creative hub" ||
    lower === "personal vault" ||
    lower === "corsair vengeance" ||
    lower === "corsair vengeance lpx" ||
    lower.includes("request failed") ||
    lower.includes("temporarily unavailable") ||
    lower.includes("overloaded")
  );
}

function titleCaseWords(value: string): string {
  const keepUpper = new Set([
    "PC",
    "AIO",
    "SSD",
    "HDD",
    "RAM",
    "CPU",
    "GPU",
    "BIOS",
    "UEFI",
    "USB",
    "HDMI",
    "NVME",
    "M.2",
    "DDR4",
    "DDR5",
    "CMD",
    "PDF",
    "DOCX",
    "OS",
    "PSU",
    "CMOS",
    "VPN",
    "AV",
    "IC",
  ]);

  return value
    .split(" ")
    .map((word) => {
      const cleaned = word.trim();
      if (!cleaned) return cleaned;
      const normalized = cleaned.toUpperCase().replace(/[.,;:]+$/g, "");
      if (keepUpper.has(normalized)) return normalized + cleaned.slice(normalized.length);
      if (/^\d/.test(cleaned)) return cleaned;
      if (/^[A-Z0-9]+[-][A-Z0-9-]+$/.test(cleaned)) return cleaned.toUpperCase();
      return cleaned.charAt(0).toUpperCase() + cleaned.slice(1).toLowerCase();
    })
    .join(" ");
}

function cleanSubjectCandidate(value: string): string {
  let cleaned = cleanFilePart(value)
    .replace(/\b(i|we)\s+(need|want|should|have|am|are|was|were|will)\b/gi, "")
    .replace(/\b(tell me|find|give me|let me know|what it costs|what does it cost)\b/gi, "")
    .replace(/\s+/g, " ")
    .trim();

  cleaned = cleaned
    .replace(/^(the|a|an|this|that|and|or|but|with|for|about)\s+/i, "")
    .replace(/[.,;:\-–—\s]+$/g, "")
    .trim();

  if (cleaned.length > 70) {
    cleaned = cleaned.slice(0, 70).replace(/\s+\S*$/, "").trim();
  }

  return titleCaseWords(cleaned);
}

function firstMeaningfulUserText(messages: ExportMessage[]): string {
  const userMessages = messages
    .filter((m) => m.role === "user")
    .map((m) => (m.content || "").trim())
    .filter(Boolean);

  const usable = userMessages.find((content) => {
    const lower = content.toLowerCase();
    return (
      content.length > 8 &&
      !lower.includes("request failed") &&
      !lower.includes("temporarily unavailable") &&
      !lower.includes("overloaded")
    );
  });

  return usable || "";
}

function extractProbablePeopleNamesFromText(text: string): string[] {
  const excluded = new Set([
    "rodgers pc",
    "rodgerspc",
    "google drive",
    "microsoft windows",
    "command center",
    "task manager",
    "tech assistant",
    "central texas",
    "bell county",
    "customer name",
    "joshua rodgers",
    "ashley rodgers",
    "rodgerspc ai",
    "joshua bench",
    "google docs",
    "google workspace",
    "dell optiplex",
    "samsung ssd",
    "seagate hdd",
    "windows repair",
    "google inc",
  ]);

  const names: string[] = [];
  const pushName = (raw: string) => {
    const candidate = cleanFilePart(raw)
      .replace(/\b(job|log|record|customer|client|last|prior|previous|brought|came|comes)\b/gi, "")
      .replace(/\s+/g, " ")
      .trim();
    const lower = candidate.toLowerCase();

    if (
      candidate.length >= 5 &&
      candidate.length <= 45 &&
      /[A-Za-z]/.test(candidate) &&
      !excluded.has(lower) &&
      !lower.includes("joshua") &&
      !lower.includes("rodgerspc") &&
      !lower.includes("command center") &&
      !lower.includes("google") &&
      !lower.includes("windows") &&
      !isLikelyProductOrSystemName(candidate) &&
      !names.some((name) => name.toLowerCase() === lower)
    ) {
      names.push(candidate);
    }
  };

  const fieldPatterns = [
    /customer\s*:\s*([A-Za-z\s'\-.]+)/gi,
    /client\s*:\s*([A-Za-z\s'\-.]+)/gi,
    /client\s*name\s*:\s*([A-Za-z\s'\-.]+)/gi,
    /customer\s*name\s*:\s*([A-Za-z\s'\-.]+)/gi,
    /contact\s*:\s*([A-Za-z\s'\-.]+)/gi,
    /customer\s+([A-Z][a-z]+\s+[A-Z][a-z]+)/g,
    /customer\s+([A-Z][a-z]+\s+[A-Z][a-z]+)\s+brought\s+in/gi,
    /customer\s+([A-Z][a-z]+\s+[A-Z][a-z]+)\s+came\s+in/gi,
  ];

  fieldPatterns.forEach((regex) => {
    let match: RegExpExecArray | null;
    while ((match = regex.exec(text)) !== null) {
      pushName(match[1] || "");
    }
  });

  const contextualPatterns = [
    /\b(?:for|on|about|regarding|lookup|look up|pull|find|review)\s+([A-Z][a-z]+\s+[A-Z][a-z]+)(?:'s|\s+(?:job|log|record|machine|computer|pc|laptop|desktop|aio))?\b/g,
    /\b([A-Z][a-z]+\s+[A-Z][a-z]+)(?:'s)?\s+(?:job|log|record|machine|computer|pc|laptop|desktop|aio)\b/g,
  ];

  contextualPatterns.forEach((regex) => {
    let match: RegExpExecArray | null;
    while ((match = regex.exec(text)) !== null) {
      pushName(match[1] || "");
    }
  });

  return names.slice(0, 6);
}

function extractDeviceIssueSubject(text: string): string {
  const raw = textFromMessages([{ id: "all", role: "user", content: text, timestamp: "" }]);
  const combined = raw.toLowerCase();

  const devicePatterns: Array<[RegExp, (m: RegExpMatchArray) => string]> = [
    [/(dell\s+optiplex\s+5060)[\s\S]{0,120}(idle|shut|turns? off|shutdown|power off)/i, (m) => `${m[1]} Idle Shutdown`],
    [/(dell\s+optiplex\s+\d{4})[\s\S]{0,120}(idle|shut|turns? off|shutdown|power off)/i, (m) => `${m[1]} Shutdown Issue`],
    [/(hp\s+pavilion\s+24[-\w]*)[\s\S]{0,140}(display|screen|internal video|no internal)/i, (m) => `${m[1]} Internal Display Issue`],
    [/(xbox\s+series\s+x)[\s\S]{0,120}(hdmi|no video)/i, (m) => `${m[1]} HDMI Repair`],
    [/(ps5|playstation\s*5)[\s\S]{0,120}(hdmi|clean|liquid metal|no video)/i, (m) => `${m[1]} Console Repair`],
    [/(lenovo\s+ideapad\s*5)[\s\S]{0,140}(hinge|screen|motherboard|no power|display)/i, (m) => `${m[1]} Repair`],
    [/(seagate|st1000dm|1tb\s+seagate|failing\s+drive|damaged\s+drive)[\s\S]{0,160}(data|recover|recovery|spreadsheet|user folder|rsync|ddrescue)/i, () => "Failing Drive Data Recovery"],
    [/(samsung\s+ssd|870\s+evo|980\s+1tb)[\s\S]{0,120}(clone|recovery|destination|target)/i, () => "Drive Recovery Target Setup"],
  ];

  for (const [regex, formatter] of devicePatterns) {
    const match = raw.match(regex);
    if (match) return cleanSubjectCandidate(formatter(match));
  }

  const heuristicSubjects: Array<[RegExp, string]> = [
    [/task\s*manager[\s\S]{0,160}(startup|start\s*up)[\s\S]{0,160}(close|crash|closes|crashing)/i, "Task Manager Startup Tab Crash"],
    [/(startup|start\s*up)[\s\S]{0,160}(task\s*manager)[\s\S]{0,160}(close|crash|closes|crashing)/i, "Task Manager Startup Tab Crash"],
    [/startup[\s\S]{0,120}(apps|items|programs)[\s\S]{0,120}(disable|review|turn off)/i, "Startup App Review"],
    [/(warranty|service plans)[\s\S]{0,260}(amazon|ebay|delivery|price|vendor|seller|ssd|nvme|ram|memory|part)/i, "Warranty & Vendor Lookup"],
    [/(amazon|ebay|vendor)[\s\S]{0,180}(warranty|service plans)/i, "Warranty & Vendor Lookup"],
    [/(corsair|vengeance)[\s\S]{0,120}(ddr4|ddr5|ram|memory)/i, "RAM Compatibility and Price Check"],
    [/(amazon|price|delivery|stock)[\s\S]{0,120}(ram|memory|ssd|part|board|motherboard)/i, "Parts Price and Availability Check"],
    [/(ransomware|lockbit|encrypted)/i, "Ransomware Recovery"],
    [/(data recovery|recover files|recover data|drive recovery|user folder|rsync|ddrescue)/i, "Data Recovery"],
    [/(security tune|virus|malware|remote tool|screenconnect|ultraviewer|ccleaner|subscription|uninstall)/i, "Security Tune-Up"],
    [/(in-place|repair install|windows repair)/i, "Windows Repair"],
    [/(fresh install|windows reinstall|wipe and install)/i, "Windows Reinstall"],
    [/(ssd upgrade|clone|cloning|m\.2|nvme)/i, "SSD Upgrade"],
    [/(ram test|memory test|memory management|bad ram)/i, "RAM Testing"],
    [/(bios|uefi|firmware|cmos)/i, "BIOS and Power Settings Review"],
    [/(hdmi|xbox|playstation|ps5|series x)/i, "Console Repair"],
    [/(motherboard|board level|mosfet|charging ic|no power)/i, "Board-Level Diagnostics"],
    [/(battery|charging|charger|dc jack)/i, "Charging Repair"],
    [/(hinge|hinges|palmrest|palm rest)/i, "Hinge Repair"],
    [/(screen|lcd|digitizer|display|internal monitor|internal video|no internal display|aio)/i, "Display Diagnostics"],
    [/(unexpected shutdown|idle shutdown|turns off|shutting down|kernel-power)/i, "Unexpected Shutdown Diagnostics"],
  ];

  for (const [regex, label] of heuristicSubjects) {
    if (regex.test(combined)) return label;
  }

  return "";
}


function extractRetailProductNameForExport(text: string): string {
  const raw = String(text || "").replace(/\r/g, " ");

  const directPatterns: RegExp[] = [
    /\b(Samsung\s+990\s+PRO\s+2\s*TB\s+NVMe\s+SSD)\b/i,
    /\b(Samsung\s+990\s+PRO\s+SSD\s+2\s*TB[^.\n]*)/i,
    /\b(Samsung\s+990\s+PRO[^.\n]{0,80})/i,
    /\b((?:Crucial|Kingston|WD|Western Digital|SanDisk|Seagate|Toshiba|PNY|Teamgroup|Corsair|G\.Skill|Samsung)[^.\n]{0,120}\b(?:SSD|NVMe|M\.2|RAM|Memory|DDR4|DDR5)\b[^.\n]{0,80})/i,
  ];

  for (const pattern of directPatterns) {
    const match = raw.match(pattern);
    if (match?.[1]) {
      return cleanSubjectCandidate(match[1])
        .replace(/\bCheck Both\b/gi, "")
        .replace(/\bCurrent Price\b/gi, "")
        .replace(/\bSeller Source\b/gi, "")
        .replace(/\bDelivery Info\b/gi, "")
        .replace(/\s+/g, " ")
        .trim();
    }
  }

  const line = raw
    .split(/\n+/)
    .map((part) => part.trim())
    .find((part) => /(amazon|ebay|vendor|price|stock|delivery|seller|source)/i.test(part) && /(ssd|nvme|m\.2|ram|memory|ddr4|ddr5|battery|charger|screen|keyboard|motherboard)/i.test(part));

  if (!line) return "";

  const cleaned = cleanSubjectCandidate(line)
    .replace(/\b(also|need|quick|parts|vendor|check|current|price|condition|seller|source|delivery|info|safest|recommendation|customer|tell)\b/gi, "")
    .replace(/\b(amazon|ebay|both)\b/gi, "")
    .replace(/\s+/g, " ")
    .trim();

  return cleaned.length >= 5 ? cleaned : "";
}

function inferWarrantyVendorSubject(text: string): string {
  const normalized = normalizedExportText(text);
  const hasWarranty = normalized.includes("warranty") || normalized.includes("service plans");
  const hasVendor = normalized.includes("amazon") || normalized.includes("ebay") || normalized.includes("vendor") || normalized.includes("price") || normalized.includes("delivery") || normalized.includes("seller");
  const product = extractRetailProductNameForExport(text);

  if (hasWarranty && product) {
    return cleanSubjectCandidate(`Warranty & ${product} Vendor Lookup`);
  }

  if (product && hasVendor) {
    return cleanSubjectCandidate(`${product} Vendor Lookup`);
  }

  if (hasWarranty && hasVendor) {
    return "Warranty & Vendor Lookup";
  }

  return "";
}

function inferSubjectFromMessages(messages: ExportMessage[]): string {
  const userText = firstMeaningfulUserText(messages);
  const allText = textFromMessages(messages);
  const combinedRaw = `${userText}\n${allText}`;
  const normalized = normalizedExportText(combinedRaw);
  const probableNames = extractProbablePeopleNamesFromText(combinedRaw);
  const hasMultipleCustomerRecords = probableNames.length > 1;

  const warrantyVendorSubject = inferWarrantyVendorSubject(combinedRaw);
  if (warrantyVendorSubject) {
    return warrantyVendorSubject;
  }

  const explicitSubjectPatterns = [
    /(?:subject|task|ticket|issue|problem|repair)\s*:\s*([^\n\r]+)/i,
    /(?:regarding|about)\s*:\s*([^\n\r]+)/i,
  ];

  for (const regex of explicitSubjectPatterns) {
    const match = allText.match(regex);
    const candidate = cleanSubjectCandidate(match?.[1] || "");
    if (candidate.length > 3 && candidate.length < 75 && !isBadSubjectFallback(candidate)) {
      return candidate;
    }
  }

  if (hasMultipleCustomerRecords) {
    if (/(image|picture|screenshot|photo|visual|shown|show me|confirm|proof|evidence)/i.test(combinedRaw)) {
      return "Multi-Job Drive Evidence Review";
    }
    return "Multi-Job Drive Record Review";
  }

  const deviceIssue = extractDeviceIssueSubject(combinedRaw);
  if (deviceIssue) return deviceIssue;

  if (/(image|picture|screenshot|photo|visual|shown|show me|confirm|proof|evidence)/i.test(combinedRaw)) {
    return "Drive Job Evidence Review";
  }

  if (/(look up|lookup|pull|find|review|what we ended up doing|what did we do|last job|prior job|previous job|job log|job record|drive record|drive file)/i.test(combinedRaw)) {
    return "Drive Job Record Review";
  }

  if (normalized.includes("warranty") && normalized.includes("service")) {
    return "Warranty Record Review";
  }

  if (userText) {
    const firstSentence =
      userText
        .replace(/\n+/g, " ")
        .split(/[.!?]/)
        .map((part) => part.trim())
        .find((part) => part.length > 10) || userText;

    const candidate = cleanSubjectCandidate(firstSentence);
    if (candidate.length > 3 && !isBadSubjectFallback(candidate) && !isLikelyProductOrSystemName(candidate)) {
      return candidate;
    }
  }

  return "Command Center Export";
}


function getDriveEvidenceTextForExport(messages: ExportMessage[]): string {
  const summary = getDriveEvidenceSummaryFromMessages(messages);
  if (!summary || typeof summary !== "object") return "";

  const files = getDriveEvidenceFiles(summary);
  const parts: string[] = [];

  files.forEach((file: any) => {
    parts.push(String(file?.path || ""));
    parts.push(String(file?.name || ""));
  });

  return parts.join("\n");
}

function extractHighestKnownBlogNumber(messages: ExportMessage[]): number {
  const combined = `${textFromMessages(messages)}\n${getDriveEvidenceTextForExport(messages)}`;
  const numbers: number[] = [];
  const patterns = [
    /\bBLOG[-\s_]*(\d{1,3})\b/gi,
    /\bBlog\s*(\d{1,3})\b/gi,
    /\bblog\s*#\s*(\d{1,3})\b/gi,
  ];

  patterns.forEach((regex) => {
    let match: RegExpExecArray | null;
    while ((match = regex.exec(combined)) !== null) {
      const value = Number(match[1]);
      if (Number.isFinite(value) && value > 0 && value < 500) {
        numbers.push(value);
      }
    }
  });

  return numbers.length ? Math.max(...numbers) : 0;
}

function formatBlogNumber(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return "Blog Draft";
  return `Blog ${String(value).padStart(3, "0")}`;
}

function cleanBlogTitleCandidate(value: string): string {
  let cleaned = cleanFilePart(value)
    .replace(/^best\s+next\s+blog\s+post\s*:?\s*/i, "")
    .replace(/^confirm\s+this\s+topic\s*:?\s*/i, "")
    .replace(/^topic\s*:?\s*/i, "")
    .replace(/^title\s*:?\s*/i, "")
    .replace(/[“”"]/g, "")
    .replace(/\s+/g, " ")
    .trim();

  cleaned = cleaned
    .replace(/[.!?]+$/g, "")
    .replace(/\s+[–-]\s+rodgerspc.*$/i, "")
    .trim();

  if (cleaned.length > 90) {
    cleaned = cleaned.slice(0, 90).replace(/\s+\S*$/, "").trim();
  }

  return titleCaseWords(cleaned);
}

function extractBlogTitleFromMessages(messages: ExportMessage[]): string {
  const combined = textFromMessages(messages);

  const quotedPatterns: RegExp[] = [
    /Best\s+Next\s+Blog\s+Post\s*:\s*["“]([^"”\n]+)["”]/i,
    /Confirm\s+this\s+topic\s*:\s*["“]([^"”\n]+)["”]/i,
    /Blog\s+Post\s*:\s*["“]([^"”\n]+)["”]/i,
    /SEO\s+title\s*:\s*["“]?([^"”\n]+)["”]?/i,
    /Title\s*:\s*["“]?([^"”\n]+)["”]?/i,
  ];

  for (const pattern of quotedPatterns) {
    const match = combined.match(pattern);
    const candidate = cleanBlogTitleCandidate(match?.[1] || "");
    if (candidate.length >= 8) return candidate;
  }

  const headingPatterns: RegExp[] = [
    /Best\s+Next\s+Blog\s+Post\s*:\s*([^\n\r]+)/i,
    /^#{1,4}\s+(.+?)$/m,
  ];

  for (const pattern of headingPatterns) {
    const match = combined.match(pattern);
    const candidate = cleanBlogTitleCandidate(match?.[1] || "");
    if (candidate.length >= 8 && !candidate.toLowerCase().includes("og image")) {
      return candidate;
    }
  }

  const subject = extractDeviceIssueSubject(combined);
  if (subject && subject !== "Command Center Export") return cleanBlogTitleCandidate(subject);

  const userText = firstMeaningfulUserText(messages);
  if (/next\s+.*blog|blog\s+post|post\s+be/i.test(userText)) {
    return "Next RodgersPC Blog Topic";
  }

  return "RodgersPC Blog Draft";
}

function inferBlogExportName(messages: ExportMessage[]): { customer: string; subject: string; fileName: string } {
  const title = extractBlogTitleFromMessages(messages);
  const highestKnown = extractHighestKnownBlogNumber(messages);
  const nextNumber = highestKnown > 0 ? highestKnown + 1 : 0;
  const blogNumber = formatBlogNumber(nextNumber);
  const date = todayIsoFileDate();

  const cleanTitle = cleanBlogTitleCandidate(title) || "RodgersPC Blog Draft";
  const fileName = cleanFilePart(`${date} – ${blogNumber} – ${cleanTitle}`);

  return {
    customer: "RodgersPC Blog",
    subject: `${blogNumber} – ${cleanTitle}`,
    fileName,
  };
}


function extractIntelligentName(messages: ExportMessage[]): { customer: string; subject: string; leadSource: string } {
  let customer = "";
  let leadSource = "";

  const allText = textFromMessages(messages);
  const probableNames = extractProbablePeopleNamesFromText(allText);
  const internalTest = isInternalTestOrMixedLookup(messages);

  if (internalTest) {
    customer = "RodgersPC Internal";
  } else if (probableNames.length > 1) {
    customer = "Multiple Jobs";
  } else if (probableNames.length === 1) {
    customer = probableNames[0];
  }

  if (!customer) {
    const nameRegex = /\b([A-Z][a-z]+ [A-Z][a-z]+)\b/g;
    const excluded = [
      "Rodgers Pc",
      "Google Drive",
      "Microsoft Windows",
      "Command Center",
      "Task Manager",
      "Tech Assistant",
      "Bench Tech",
      "Front Desk",
      "Central Texas",
      "Google Docs",
      "Dell Optiplex",
      "Samsung Ssd",
      "Seagate Hdd",
      "Corsair Vengeance",
    ];
    let match: RegExpExecArray | null;

    while ((match = nameRegex.exec(allText)) !== null) {
      const candidate = cleanFilePart(match[1]);
      const lower = candidate.toLowerCase();

      if (
        candidate.length > 2 &&
        candidate.length < 40 &&
        !excluded.some((x) => x.toLowerCase() === lower) &&
        !lower.includes("joshua") &&
        !lower.includes("rodgers") &&
        !lower.includes("google") &&
        !lower.includes("windows") &&
        !isLikelyProductOrSystemName(candidate)
      ) {
        customer = candidate;
        break;
      }
    }
  }

  const subject = inferSubjectFromMessages(messages);

  const sourceMatch = allText.match(
    /\b(yelp|walk-in|walk in|website|website contact|website contact submission|contact submission|google maps|phone call|referral|website form|yelp lead)\b/i,
  );

  if (sourceMatch?.[1]) {
    leadSource = sourceMatch[1]
      .replace(/-/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }

  return { customer, subject, leadSource };
}

function isTruncatedPayloadPlaceholder(value: unknown): boolean {
  const text = String(value || "");
  return (
    text.includes("[Payload size optimized]") ||
    text.includes("Payload size optimized") ||
    text.includes("...[Payload") ||
    text.includes("... [Payload") ||
    text.includes("Attachment metadata only in model payload") ||
    text.includes("not stored in cloud backup") ||
    text.includes("restored without full media payload")
  );
}

function parseDataUrl(value: unknown): { mimeType: string; dataUrl: string; base64: string } | null {
  const raw = String(value || "").trim();

  if (!raw || isTruncatedPayloadPlaceholder(raw)) return null;

  const match = raw.match(/^data:([^;]+);base64,([A-Za-z0-9+/=\s]+)$/);
  if (!match?.[1] || !match?.[2]) return null;

  const mimeType = match[1].trim().toLowerCase();
  const base64 = match[2].replace(/\s+/g, "");

  // Avoid writing broken one-line placeholders or tiny non-images as valid evidence.
  if (base64.length < 40) return null;
  if (!/^[A-Za-z0-9+/]+={0,2}$/.test(base64)) return null;

  return {
    mimeType,
    base64,
    dataUrl: `data:${mimeType};base64,${base64}`,
  };
}

function getAttachmentMime(att: ExportAttachment): string {
  const parsed = parseDataUrl(att.base64) || parseDataUrl(att.content);
  if (parsed?.mimeType) return parsed.mimeType;
  if (att.mimeType) return att.mimeType.toLowerCase();
  return att.type || "application/octet-stream";
}

function getAttachmentDataUrl(att: ExportAttachment): string {
  const parsed = parseDataUrl(att.base64) || parseDataUrl(att.content);
  return parsed?.dataUrl || "";
}

function getGeneratedImageAssetSrc(asset?: GeneratedImageAsset | null): NamedAsset | null {
  if (!asset) return null;

  const parsedDataUrl = parseDataUrl(asset.dataUrl);
  if (parsedDataUrl && parsedDataUrl.mimeType.startsWith("image/")) {
    return {
      label: "Generated image asset",
      src: parsedDataUrl.dataUrl,
      mimeType: parsedDataUrl.mimeType,
      fileName: asset.name || "generated-image",
    };
  }

  const url = String(asset.url || "").trim();
  if (
    url &&
    !isTruncatedPayloadPlaceholder(url) &&
    (url.startsWith("data:image/") || url.startsWith("blob:") || /^https?:\/\//i.test(url))
  ) {
    return {
      label: "Generated image asset",
      src: url,
      mimeType: asset.mimeType || "image/png",
      fileName: asset.name || "generated-image",
    };
  }

  return null;
}

function collectMessageAssets(msg: ExportMessage): NamedAsset[] {
  const assets: NamedAsset[] = [];

  const generatedAsset = getGeneratedImageAssetSrc(msg.imageAsset);
  if (generatedAsset) {
    assets.push(generatedAsset);
  }

  const imageUrlPayload = parseDataUrl(msg.imageUrl);
  if (imageUrlPayload && imageUrlPayload.mimeType.startsWith("image/") && !generatedAsset) {
    assets.push({
      label: "Generated / attached image",
      src: imageUrlPayload.dataUrl,
      mimeType: imageUrlPayload.mimeType,
      fileName: "chat-image",
    });
  }

  (msg.attachments || []).forEach((att, idx) => {
    const src = getAttachmentDataUrl(att);
    const mimeType = getAttachmentMime(att);
    if (!src) return;

    if (mimeType.startsWith("image/") || mimeType === "application/pdf") {
      assets.push({
        label: mimeType === "application/pdf" ? "Attached PDF" : "Attached image",
        src,
        mimeType,
        fileName: att.name || `attachment-${idx + 1}`,
      });
    }
  });

  return assets;
}

function hasAnyVisualPayload(messages: ExportMessage[]): boolean {
  return messages.some((msg) => collectMessageAssets(msg).length > 0);
}

function messageTextForExport(msg: ExportMessage): string {
  const parts = [msg.content || ""];

  if (msg.commands?.length) {
    parts.push(
      msg.commands
        .map((cmd, idx) => `[Command ${idx + 1}] ${cmd.title || cmd.type || "Command"}\n${cmd.code || ""}`)
        .join("\n\n"),
    );
  }

  if (msg.reports?.length) {
    parts.push(
      msg.reports
        .map((report, idx) => `[Report ${idx + 1}] ${report.title || "Report"}\n${report.text || ""}`)
        .join("\n\n"),
    );
  }

  return parts.filter(Boolean).join("\n\n");
}


function getDriveEvidenceSummaryFromMessages(messages: ExportMessage[]): any | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const summary = messages[i]?.driveEvidenceSummary;
    if (summary && typeof summary === "object") return summary;
  }
  return null;
}

function getDriveEvidenceFiles(summary: any): any[] {
  if (!summary || typeof summary !== "object") return [];

  if (Array.isArray(summary.loadedDriveFiles)) return summary.loadedDriveFiles;
  if (Array.isArray(summary.files)) return summary.files;
  if (Array.isArray(summary.loadedFiles)) return summary.loadedFiles;

  return [];
}

function readFiniteNumber(...values: any[]): number {
  for (const value of values) {
    const numberValue = Number(value);
    if (Number.isFinite(numberValue) && numberValue >= 0) return numberValue;
  }

  return 0;
}

function countLoadedDriveFiles(summary: any): number {
  if (!summary || typeof summary !== "object") return 0;

  const direct = readFiniteNumber(
    summary.loadedDriveFileCount,
    summary.loadedFileCount,
    summary.loadedFiles,
    summary.loadedDriveFilesCount,
  );

  if (direct > 0) return direct;

  return getDriveEvidenceFiles(summary).filter((file: any) => {
    const textChars = Number(file?.textChars ?? file?.contentChars ?? 0);
    const visualCount = Number(file?.visualPayloadCount ?? file?.visualPayloads ?? file?.imageCount ?? 0);
    const hasText = Boolean(file?.hasReadableText || file?.hasLoadedText || file?.textLoaded || file?.hasText || file?.contentLoaded);
    const hasVision = Boolean(file?.canClaimVisualInspection || file?.visualPayloadsSent || file?.visualSent === "yes") || (Number.isFinite(visualCount) && visualCount > 0);

    return hasText || hasVision || (Number.isFinite(textChars) && textChars > 0);
  }).length;
}

function countDriveVisualPayloads(summary: any): number {
  if (!summary || typeof summary !== "object") return 0;

  const direct = readFiniteNumber(
    summary.loadedDriveVisualPayloadCount,
    summary.visualPayloadsSentCount,
    summary.driveVisualPayloadsSent,
    summary.visualPayloadsSent,
    summary.inlineVisionPayloads,
  );

  if (direct > 0) return direct;

  return getDriveEvidenceFiles(summary).reduce((sum: number, file: any) => {
    const count = Number(
      file?.visualPayloadCount ??
      file?.visualPayloads ??
      file?.visionPayloadsSent ??
      file?.imageCount ??
      (Array.isArray(file?.images) ? file.images.length : 0),
    );

    return sum + (Number.isFinite(count) && count > 0 ? count : 0);
  }, 0);
}

function countDriveTextFiles(summary: any): number {
  if (!summary || typeof summary !== "object") return 0;

  const direct = readFiniteNumber(
    summary.loadedDriveTextFileCount,
    summary.loadedTextFileCount,
    summary.loadedTextFiles,
    summary.textFilesLoaded,
  );

  if (direct > 0) return direct;

  return getDriveEvidenceFiles(summary).filter((file: any) => {
    const textChars = Number(file?.textChars ?? file?.contentChars ?? 0);
    return Boolean(file?.hasReadableText || file?.hasLoadedText || file?.textLoaded || file?.hasText || file?.contentLoaded) ||
      (Number.isFinite(textChars) && textChars > 0);
  }).length;
}

function getDriveLoadFailures(summary: any): any[] {
  if (!summary || typeof summary !== "object") return [];

  const failureArrays = [
    summary.failedDriveLoads,
    summary.loadFailures,
    summary.failedLoads,
    summary.failedFilesList,
    summary.driveLoadFailures,
  ].filter(Array.isArray) as any[][];

  const merged: any[] = [];
  const seen = new Set<string>();

  failureArrays.flat().forEach((failure: any) => {
    if (!failure || typeof failure !== "object") return;

    const path = String(failure.path || failure.name || failure.fileName || failure.fileId || "Unknown Drive file");
    const error = String(failure.error || failure.reason || failure.message || failure.status || "load failed");
    const engine = String(failure.engine || failure.retrievalEngine || "unknown");
    const key = `${path}|${error}|${engine}`;

    if (!seen.has(key)) {
      seen.add(key);
      merged.push(failure);
    }
  });

  return merged;
}

function countFailedDriveLoads(summary: any): number {
  if (!summary || typeof summary !== "object") return 0;

  const failureDetails = getDriveLoadFailures(summary);
  if (failureDetails.length > 0) return failureDetails.length;

  const direct = readFiniteNumber(
    summary.failedDriveLoadCount,
    summary.failedFiles,
    summary.loadFailedFiles,
    summary.failedLoadCount,
  );

  if (direct > 0) return direct;

  const failedFiles = getDriveEvidenceFiles(summary).filter((file: any) => {
    const status = String(file?.extractionStatus || file?.evidenceStatus || "").toLowerCase();
    return status.includes("failed") || status.includes("load_failed");
  }).length;

  return failedFiles;
}

function describeDriveEvidenceForExport(summary: any): string {
  if (!summary || typeof summary !== "object") return "No Drive evidence summary was attached to this chat export.";

  const loadedFiles = countLoadedDriveFiles(summary);
  const textFiles = countDriveTextFiles(summary);
  const visualPayloads = countDriveVisualPayloads(summary);
  const failedFiles = countFailedDriveLoads(summary);

  const parts = [
    `Loaded Drive files: ${loadedFiles}`,
    `Loaded text files: ${textFiles}`,
    `Drive visual payloads sent: ${visualPayloads}`,
  ];

  if (failedFiles > 0) parts.push(`Failed Drive loads: ${failedFiles}`);

  const metadataOnly = Number(summary.metadataOnlyFocusedFileCount ?? summary.metadataOnlyFileCount ?? 0);
  if (Number.isFinite(metadataOnly) && metadataOnly > 0) parts.push(`Focused metadata-only files: ${metadataOnly}`);

  return parts.join(" | ");
}

function driveEvidenceDetailLines(summary: any): string[] {
  if (!summary || typeof summary !== "object") return [];

  const lines: string[] = [];
  const files = getDriveEvidenceFiles(summary);
  const failures = getDriveLoadFailures(summary);

  if (files.length > 0) {
    lines.push("Loaded/read Drive files:");
    files.slice(0, 18).forEach((file: any, index: number) => {
      const path = String(file?.path || file?.name || `Drive file ${index + 1}`);
      const evidenceStatus = String(file?.evidenceStatus || file?.extractionStatus || "unknown");
      const textLoaded = Boolean(file?.hasReadableText || file?.hasLoadedText || file?.textLoaded || file?.hasText || file?.contentLoaded);
      const textChars = Number(file?.textChars ?? file?.contentChars ?? 0);
      const visualPayloadCount = Number(file?.visualPayloadCount ?? file?.visualPayloads ?? file?.visionPayloadsSent ?? file?.imageCount ?? (Array.isArray(file?.images) ? file.images.length : 0));
      const visualCount = Number.isFinite(visualPayloadCount) && visualPayloadCount > 0 ? visualPayloadCount : 0;
      const source = String(file?.source || file?.retrievalEngine || "drive");
      const engine = String(file?.retrievalEngine || "unknown");

      lines.push(
        `${index + 1}. ${path} | source=${source} | engine=${engine} | evidence=${evidenceStatus} | text=${textLoaded || textChars > 0 ? "loaded" : "none"}${textChars > 0 ? ` (${textChars} chars)` : ""} | visual payloads=${visualCount}`,
      );
    });

    if (files.length > 18) {
      lines.push(`... ${files.length - 18} additional loaded Drive evidence file(s) omitted from export summary.`);
    }
  } else {
    lines.push("Loaded/read Drive files: None");
  }

  if (failures.length > 0) {
    lines.push("Attempted/failed Drive file loads:");
    failures.slice(0, 18).forEach((failure: any, index: number) => {
      const path = String(failure?.path || failure?.name || failure?.fileName || failure?.fileId || "Unknown Drive file");
      const engine = String(failure?.engine || failure?.retrievalEngine || "unknown");
      const error = String(failure?.error || failure?.reason || failure?.message || failure?.status || "load failed");

      lines.push(`Failed load ${index + 1}: ${path} | engine=${engine} | error=${error}`);
    });

    if (failures.length > 18) {
      lines.push(`... ${failures.length - 18} additional failed Drive load(s) omitted from export summary.`);
    }
  } else {
    lines.push("Attempted/failed Drive file loads: None");
  }

  return lines;
}

function driveEvidenceDetailText(summary: any): string {
  const lines = driveEvidenceDetailLines(summary);
  if (!lines.length) return "";
  return lines.join("\n");
}

function driveEvidenceDetailHtml(summary: any): string {
  const lines = driveEvidenceDetailLines(summary);
  if (!lines.length) return "";

  return `
    <div class="evidence-detail">
      <div class="evidence-detail-title">Drive Evidence Detail</div>
      <ul>
        ${lines.map((line) => `<li>${escapeHtml(line)}</li>`).join("")}
      </ul>
    </div>
  `;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function renderInlineMarkdown(input: string): string {
  let html = escapeHtml(input || "");

  const placeholders: string[] = [];
  const hold = (value: string): string => {
    const token = `@@EXPORT_PLACEHOLDER_${placeholders.length}@@`;
    placeholders.push(value);
    return token;
  };

  // Inline code should not break product links. If the inline-code content is a URL,
  // render it as a normal clickable raw-link anchor instead of a gray code pill.
  html = html.replace(/`([^`]+)`/g, (_match, code) => {
    const codeText = String(code || "");
    if (/^https?:\/\//i.test(codeText.trim())) {
      const cleanUrl = codeText.trim().replace(/[.,;:!?]+$/g, "");
      const trailing = codeText.trim().slice(cleanUrl.length);
      return hold(`<a href="${escapeAttr(cleanUrl)}">${escapeHtml(cleanUrl)}</a>${escapeHtml(trailing)}`);
    }

    return hold(`<code>${codeText}</code>`);
  });

  // Convert valid markdown links first and protect them before raw URL linking.
  html = html.replace(/\[([^\]\n]{1,160})\]\((https?:\/\/[^\s)]+)\)/g, (_m, label, url) => {
    const cleanUrl = String(url || "").replace(/[.,;:!?]+$/g, "");
    return hold(`<a href="${escapeAttr(cleanUrl)}">${label}</a>`);
  });

  // Convert raw URLs. Because markdown anchors are protected above, this cannot nest anchors.
  html = html.replace(/(https?:\/\/[^\s<]+)/g, (url) => {
    const cleanUrl = String(url || "").replace(/[.,;:!?]+$/g, "");
    const trailing = String(url || "").slice(cleanUrl.length);
    return hold(`<a href="${escapeAttr(cleanUrl)}">${escapeHtml(cleanUrl)}</a>${escapeHtml(trailing)}`);
  });

  html = html.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  html = html.replace(/\*([^*]+)\*/g, "<em>$1</em>");

  placeholders.forEach((value, index) => {
    html = html.replace(new RegExp(escapeRegExp(`@@EXPORT_PLACEHOLDER_${index}@@`), "g"), value);
  });

  // Defensive cleanup for malformed nested anchors produced by older chats/responses.
  html = html.replace(/href="&lt;a\s+href=&quot;(https?:\/\/[^&]+)&quot;&gt;[^\"]*"/gi, 'href="$1"');
  html = html.replace(/<a\s+href="<a\s+href="(https?:\/\/[^"]+)"[^>]*>/gi, '<a href="$1">');

  return html;
}

function loadExportImage(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    if (!src) {
      resolve(null);
      return;
    }

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

function renderMarkdownForRichExport(markdown: string): string {
  const lines = String(markdown || "").replace(/\r/g, "").split("\n");
  const output: string[] = [];
  let listOpen = false;
  let codeOpen = false;
  let codeLines: string[] = [];

  const closeList = () => {
    if (listOpen) {
      output.push("</ul>");
      listOpen = false;
    }
  };

  const closeCode = () => {
    if (codeOpen) {
      output.push(`<pre><code>${escapeHtml(codeLines.join("\n"))}</code></pre>`);
      codeLines = [];
      codeOpen = false;
    }
  };

  for (const rawLine of lines) {
    const line = rawLine.replace(/\s+$/g, "");

    if (/^```/.test(line.trim())) {
      if (codeOpen) closeCode();
      else {
        closeList();
        codeOpen = true;
        codeLines = [];
      }
      continue;
    }

    if (codeOpen) {
      codeLines.push(rawLine);
      continue;
    }

    if (!line.trim()) {
      closeList();
      output.push('<div class="spacer"></div>');
      continue;
    }

    const heading = line.match(/^(#{1,4})\s+(.+)$/);
    if (heading) {
      closeList();
      const level = Math.min(4, heading[1].length + 1);
      output.push(`<h${level}>${renderInlineMarkdown(heading[2])}</h${level}>`);
      continue;
    }

    if (/^[-*_]{3,}\s*$/.test(line.trim())) {
      closeList();
      output.push('<hr />');
      continue;
    }

    const bullet = line.match(/^\s*(?:[-*•]|\d+[.)])\s+(.+)$/);
    if (bullet) {
      if (!listOpen) {
        output.push("<ul>");
        listOpen = true;
      }
      output.push(`<li>${renderInlineMarkdown(bullet[1])}</li>`);
      continue;
    }

    closeList();
    output.push(`<p>${renderInlineMarkdown(line)}</p>`);
  }

  closeCode();
  closeList();

  return output.join("\n");
}

export default function AIExporterModal({
  isOpen,
  onClose,
  messages,
  currentMode,
  onUploadFile,
  driveSyncEnabled,
  globalTheme = "rodgerspc",
}: AIExporterModalProps) {
  const [customerName, setCustomerName] = useState("");
  const [logTopic, setLogTopic] = useState("");
  const [fileName, setFileName] = useState("");
  const [isManualEdit, setIsManualEdit] = useState(false);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const [lastSavedExt, setLastSavedExt] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState("");

  const canvasRef = useRef<HTMLCanvasElement>(null);

  const isLight = globalTheme === "light";
  const isDark = globalTheme === "dark";

  const generatedDate = useMemo(() => new Date().toLocaleString(), [isOpen]);
  const activeModeLabel = modeLabel(currentMode);
  const driveEvidenceSummary = useMemo(() => getDriveEvidenceSummaryFromMessages(messages), [messages]);
  const driveVisualPayloadCount = useMemo(() => countDriveVisualPayloads(driveEvidenceSummary), [driveEvidenceSummary]);
  const driveTextFileCount = useMemo(() => countDriveTextFiles(driveEvidenceSummary), [driveEvidenceSummary]);
  const userVisualPayloadPresent = useMemo(() => hasAnyVisualPayload(messages), [messages]);
  const visualPayloadPresent = userVisualPayloadPresent || driveVisualPayloadCount > 0;

  useEffect(() => {
    if (!isOpen) return;

    if (isBlogEngineMode(currentMode)) {
      const blogExport = inferBlogExportName(messages);
      setCustomerName(blogExport.customer);
      setLogTopic(blogExport.subject);
      setFileName(blogExport.fileName);
      setIsManualEdit(false);
      setSaveStatus("idle");
      setErrorMessage("");
      return;
    }

    const { customer, subject, leadSource } = extractIntelligentName(messages);
    const cleanCustomer = customer || "RodgersPC Internal";
    const cleanSubject = leadSource ? `${subject} (${leadSource})` : subject;

    setCustomerName(cleanCustomer);
    setLogTopic(cleanSubject);
    setIsManualEdit(false);
    setSaveStatus("idle");
    setErrorMessage("");
  }, [isOpen, currentMode, messages]);

  useEffect(() => {
    if (!isOpen || isManualEdit) return;

    if (isBlogEngineMode(currentMode)) {
      const blogExport = inferBlogExportName(messages);
      setFileName(blogExport.fileName);
      return;
    }

    const cleanCustomer = cleanFilePart(customerName) || "RodgersPC";
    const cleanTopic = cleanFilePart(logTopic) || "Command Center Notes";
    const date = todayFileDate();

    setFileName(`${cleanCustomer} – ${cleanTopic} – ${date}`);
  }, [customerName, logTopic, isManualEdit, isOpen, currentMode, messages]);

  if (!isOpen) return null;

  const safeFileName = cleanFilePart(fileName) || `RodgersPC Command Center Notes – ${todayFileDate()}`;

  const generateText = (): string => {
    let txt = "";
    txt += "========================================================================\n";
    txt += "                    RODGERSPC COMMAND CENTER EXPORT\n";
    txt += "========================================================================\n";
    txt += `Business: ${BUSINESS_NAME}\n`;
    txt += `Address: ${SHOP_ADDRESS}\n`;
    txt += `Generated: ${generatedDate}\n`;
    txt += `Mode: ${activeModeLabel}\n`;
    txt += `Operator: ${OPERATOR_EMAIL}\n`;
    txt += `Messages: ${messages.length}\n`;
    txt += `Visual evidence recorded: ${visualPayloadPresent ? "Yes" : "No"}\n`;
    txt += `User visual attachments embedded in rich exports: ${userVisualPayloadPresent ? "Yes" : "No"}\n`;
    txt += `Drive visual payloads sent: ${driveVisualPayloadCount}\n`;
    txt += `Drive text files loaded: ${driveTextFileCount}\n`;
    txt += `Drive evidence summary: ${describeDriveEvidenceForExport(driveEvidenceSummary)}\n`;
    const driveDetail = driveEvidenceDetailText(driveEvidenceSummary);
    if (driveDetail) {
      txt += "\nDrive evidence detail:\n";
      txt += `${driveDetail}\n`;
    }
    txt += "------------------------------------------------------------------------\n\n";

    messages.forEach((msg, index) => {
      const sender = msg.role === "user" ? userSenderLabel() : aiSenderLabelForMode(currentMode);
      txt += `[${index + 1}] [${msg.timestamp}] ${sender}\n`;
      txt += `${messageTextForExport(msg)}\n`;

      const assets = collectMessageAssets(msg);
      if (assets.length) {
        assets.forEach((asset, assetIndex) => {
          txt += `[Visual attachment ${assetIndex + 1}: ${asset.fileName} (${asset.mimeType}) included in HTML/DOC/PDF print exports]\n`;
        });
      }

      txt += "------------------------------------------------------------------------\n\n";
    });

    txt += "End of RodgersPC Command Center export.\n";
    return txt;
  };

  const generateMarkdown = (): string => {
    let md = "";
    md += "# RodgersPC Command Center Export\n\n";
    md += `**Business:** ${BUSINESS_NAME}\n\n`;
    md += `**Address:** ${SHOP_ADDRESS}\n\n`;
    md += `**Operator:** ${OPERATOR_EMAIL}\n\n`;
    md += `**Generated:** ${generatedDate}\n\n`;
    md += `**Mode:** ${activeModeLabel}\n\n`;
    md += `**Messages:** ${messages.length}\n\n`;
    md += `**Visual evidence recorded:** ${visualPayloadPresent ? "Yes" : "No"}\n\n`;
    md += `**User visual attachments embedded in rich exports:** ${userVisualPayloadPresent ? "Yes" : "No"}\n\n`;
    md += `**Drive visual payloads sent:** ${driveVisualPayloadCount}\n\n`;
    md += `**Drive text files loaded:** ${driveTextFileCount}\n\n`;
    md += `**Drive evidence summary:** ${describeDriveEvidenceForExport(driveEvidenceSummary)}\n\n`;
    const driveDetail = driveEvidenceDetailText(driveEvidenceSummary);
    if (driveDetail) {
      md += `## Drive Evidence Detail\n\n`;
      md += driveDetail
        .split("\n")
        .map((line) => `- ${line}`)
        .join("\n");
      md += "\n\n";
    }
    md += "---\n\n";

    messages.forEach((msg, index) => {
      const sender = msg.role === "user" ? userSenderLabel() : aiSenderLabelForMode(currentMode);
      md += `## ${index + 1}. ${sender}\n\n`;
      md += `**Timestamp:** ${msg.timestamp}\n\n`;
      md += `${messageTextForExport(msg)}\n\n`;

      const assets = collectMessageAssets(msg);
      assets.forEach((asset, assetIndex) => {
        md += `_Visual attachment ${assetIndex + 1}: ${asset.fileName} (${asset.mimeType}) included in HTML/DOC/PDF print exports._\n\n`;
      });

      md += "---\n\n";
    });

    md += "_Exported from RodgersPC Command Center._\n";
    return md;
  };

  const renderAssetHtml = (asset: NamedAsset, index: number): string => {
    const label = `${asset.label} ${index + 1}: ${asset.fileName}`;

    if (asset.mimeType === "application/pdf") {
      return `
        <div class="attachment-card pdf-card">
          <div class="attachment-title">${escapeHtml(label)}</div>
          <div class="attachment-note">PDF payload is embedded in the export file. Browser/Word PDF preview support may vary.</div>
          <object data="${escapeAttr(asset.src)}" type="application/pdf" class="pdf-object">
            <div class="attachment-note">PDF preview unavailable in this viewer. The original PDF payload was retained in the export source.</div>
          </object>
        </div>`;
    }

    return `
      <figure class="attachment-card">
        <div class="attachment-title">${escapeHtml(label)}</div>
        <img
        src="${escapeAttr(asset.src)}"
        alt="${escapeAttr(label)}"
        loading="eager"
        decoding="sync"
        data-export-asset="image"
        data-export-source="chat"
      />
      </figure>`;
  };

  const generateRichHtml = (exportMode: RichExportMode = "clean"): string => {
    const includeEvidenceDetail = exportMode === "evidence";
    const evidenceSummaryLine = describeDriveEvidenceForExport(driveEvidenceSummary);

    const rows = messages
      .map((msg, index) => {
        const isUser = msg.role === "user";
        const roleLabel = isUser ? "Joshua" : modeLabel(currentMode);
        const turnClass = isUser ? "user-turn" : "ai-turn";
        const messageText = messageTextForExport(msg);
        const assets = collectMessageAssets(msg);

        return `
          <section class="turn ${turnClass}">
            <div class="turn-head">
              <div class="turn-role">
                <span class="turn-index">${index + 1}</span>
                <span>${escapeHtml(roleLabel)}</span>
              </div>
              <div class="turn-time">${escapeHtml(msg.timestamp || "")}</div>
            </div>
            <div class="turn-body">${renderMarkdownForRichExport(messageText)}</div>
            ${assets.length ? `<div class="attachments">${assets.map(renderAssetHtml).join("")}</div>` : ""}
          </section>
        `;
      })
      .join("");

    return `
<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${escapeHtml(safeFileName)}</title>
  <style>
    @page {
      size: Letter;
      margin: 0.55in;
    }

    * {
      box-sizing: border-box;
    }

    body {
      margin: 0;
      background: #ffffff;
      color: #111827;
      font-family: Arial, Helvetica, sans-serif;
      font-size: 12.5px;
      line-height: 1.5;
    }

    .page {
      width: 100%;
      max-width: 940px;
      margin: 0 auto;
      padding: 26px;
    }

    .header {
      border: 1px solid #d8dee8;
      border-left: 8px solid #f6a400;
      border-radius: 14px;
      padding: 16px 18px;
      margin-bottom: 14px;
      background: #071a3d;
      color: #ffffff;
      page-break-inside: avoid;
    }

    .header-top {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 18px;
    }

    .title {
      margin: 0;
      color: #ffffff;
      font-size: 20px;
      line-height: 1.15;
      font-weight: 900;
      letter-spacing: -0.02em;
    }

    .subtitle {
      margin-top: 6px;
      color: #dbe7ff;
      font-size: 11px;
      line-height: 1.35;
    }

    .record-pill {
      flex: 0 0 auto;
      border: 1px solid rgba(246, 164, 0, 0.65);
      border-radius: 999px;
      padding: 6px 9px;
      color: #ffce73;
      background: rgba(2, 11, 30, 0.35);
      font-family: Consolas, "Courier New", monospace;
      font-size: 9px;
      font-weight: 900;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      white-space: nowrap;
    }

    .meta-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 8px;
      margin-bottom: 12px;
      page-break-inside: avoid;
    }

    .meta-card {
      min-height: 46px;
      border: 1px solid #e2e8f0;
      border-radius: 10px;
      padding: 8px 9px;
      background: #f8fafc;
    }

    .meta-label {
      margin-bottom: 4px;
      color: #64748b;
      font-family: Consolas, "Courier New", monospace;
      font-size: 8px;
      font-weight: 900;
      text-transform: uppercase;
      letter-spacing: 0.08em;
    }

    .meta-value {
      color: #071a3d;
      font-size: 11.5px;
      font-weight: 800;
      overflow-wrap: anywhere;
    }

    .notice {
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      padding: 8px 10px;
      margin-bottom: 14px;
      background: #fafafa;
      color: #475569;
      font-size: 10.5px;
      page-break-inside: avoid;
    }

    .transcript {
      display: block;
    }

    .turn {
      border: 1px solid #d8dee8;
      border-radius: 14px;
      margin-bottom: 13px;
      overflow: hidden;
      background: #ffffff;
      page-break-inside: avoid;
    }

    .turn-head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      padding: 9px 12px;
      border-bottom: 1px solid #e5e7eb;
      font-family: Consolas, "Courier New", monospace;
    }

    .turn-role {
      display: flex;
      align-items: center;
      gap: 7px;
      font-size: 11px;
      font-weight: 900;
      text-transform: uppercase;
      letter-spacing: 0.06em;
    }

    .turn-index {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 21px;
      height: 21px;
      border-radius: 999px;
      font-size: 10px;
      font-weight: 900;
    }

    .turn-time {
      color: #64748b;
      font-size: 10px;
      font-weight: 700;
      white-space: nowrap;
    }

    .user-turn {
      border-left: 6px solid #143f8f;
    }

    .user-turn .turn-head {
      background: #eef6ff;
      color: #143f8f;
    }

    .user-turn .turn-index {
      background: #143f8f;
      color: #ffffff;
    }

    .ai-turn {
      border-left: 6px solid #f6a400;
    }

    .ai-turn .turn-head {
      background: #fff8df;
      color: #7a4d00;
    }

    .ai-turn .turn-index {
      background: #f6a400;
      color: #071a3d;
    }

    .turn-body {
      padding: 12px 14px 13px;
      color: #111827;
      overflow-wrap: anywhere;
    }

    .turn-body h2,
    .turn-body h3,
    .turn-body h4,
    .turn-body h5 {
      margin: 12px 0 6px;
      color: #071a3d;
      font-weight: 900;
      line-height: 1.25;
    }

    .turn-body h2 {
      border-bottom: 1px solid #e2e8f0;
      padding-bottom: 3px;
      font-size: 15.5px;
    }

    .turn-body h3 {
      font-size: 14px;
    }

    .turn-body h4,
    .turn-body h5 {
      font-size: 13px;
    }

    .turn-body p {
      margin: 0 0 7px;
    }

    .turn-body ul {
      margin: 0 0 8px 20px;
      padding: 0;
    }

    .turn-body li {
      margin-bottom: 4px;
    }

    .turn-body strong {
      font-weight: 900;
    }

    .turn-body code {
      border: 1px solid #e2e8f0;
      border-radius: 4px;
      padding: 1px 4px;
      background: #f1f5f9;
      font-family: Consolas, "Courier New", monospace;
      font-size: 0.92em;
    }

    .turn-body pre {
      white-space: pre-wrap;
      overflow-wrap: anywhere;
      border-radius: 9px;
      padding: 10px;
      background: #071a3d;
      color: #e5eefc;
    }

    .turn-body a {
      color: #143f8f;
      font-weight: 800;
      text-decoration: underline;
    }

    .turn-body hr {
      border: 0;
      border-top: 1px solid #e2e8f0;
      margin: 12px 0;
    }

    .spacer {
      height: 5px;
    }

    .attachments {
      display: grid;
      gap: 9px;
      padding: 0 14px 13px;
    }

    .attachment-card {
      border: 1px solid #d8dee8;
      border-radius: 10px;
      padding: 8px;
      margin: 0;
      background: #ffffff;
      page-break-inside: avoid;
    }

    .attachment-title {
      margin-bottom: 5px;
      color: #143f8f;
      font-family: Consolas, "Courier New", monospace;
      font-size: 9px;
      font-weight: 900;
      text-transform: uppercase;
    }

    .attachment-card img {
      display: block;
      max-width: 100%;
      max-height: 620px;
      object-fit: contain;
      margin: 0 auto;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
    }

    .attachment-note {
      margin-top: 4px;
      color: #64748b;
      font-size: 10px;
      font-style: italic;
    }

    .pdf-object {
      width: 100%;
      height: 620px;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      margin-top: 6px;
    }

    .evidence-section {
      margin-top: 22px;
      border: 1px solid #bfdbfe;
      border-radius: 12px;
      padding: 12px;
      background: #f8fbff;
      color: #0f2f6b;
      font-size: 10px;
      page-break-inside: avoid;
    }

    .evidence-title {
      margin-bottom: 7px;
      color: #143f8f;
      font-family: Consolas, "Courier New", monospace;
      font-size: 9px;
      font-weight: 900;
      text-transform: uppercase;
      letter-spacing: 0.08em;
    }

    .evidence-detail {
      margin-top: 8px;
      border-top: 1px solid #dbeafe;
      padding-top: 8px;
      font-size: 9.5px;
    }

    .evidence-detail-title {
      margin-bottom: 5px;
      color: #143f8f;
      font-family: Consolas, "Courier New", monospace;
      font-size: 8.5px;
      font-weight: 900;
      text-transform: uppercase;
      letter-spacing: 0.08em;
    }

    .evidence-detail ul {
      margin: 0 0 0 17px;
      padding: 0;
    }

    .evidence-detail li {
      margin: 0 0 4px;
    }

    .footer {
      margin-top: 22px;
      padding-top: 10px;
      border-top: 1px solid #e2e8f0;
      color: #64748b;
      text-align: center;
      font-family: Consolas, "Courier New", monospace;
      font-size: 8.5px;
      text-transform: uppercase;
    }

    @media print {
      body {
        background: #ffffff;
      }

      .page {
        padding: 0;
        max-width: none;
      }

      .header,
      .meta-grid,
      .turn,
      .attachment-card,
      .evidence-section {
        break-inside: avoid;
      }
    }
  </style>
</head>
<body>
  <main class="page">
    <header class="header">
      <div class="header-top">
        <div>
          <h1 class="title">RodgersPC Command Center Export</h1>
          <div class="subtitle">${escapeHtml(BUSINESS_NAME)} | ${escapeHtml(SHOP_ADDRESS)}</div>
        </div>
        <div class="record-pill">${includeEvidenceDetail ? "Evidence Record" : "Clean Record"}</div>
      </div>
    </header>

    <section class="meta-grid">
      <div class="meta-card">
        <div class="meta-label">Mode</div>
        <div class="meta-value">${escapeHtml(activeModeLabel)}</div>
      </div>
      <div class="meta-card">
        <div class="meta-label">Created</div>
        <div class="meta-value">${escapeHtml(generatedDate)}</div>
      </div>
      <div class="meta-card">
        <div class="meta-label">Customer / File</div>
        <div class="meta-value">${escapeHtml(customerName || "RodgersPC")}</div>
      </div>
      <div class="meta-card">
        <div class="meta-label">Subject</div>
        <div class="meta-value">${escapeHtml(logTopic || "Command Center Notes")}</div>
      </div>
    </section>

    <div class="notice">
      Internal RodgersPC record. This export records existing chat content only. Verify before giving anything to a customer.
    </div>

    <section class="transcript">
      ${rows || `<section class="turn"><div class="turn-body">No chat messages in this export.</div></section>`}
    </section>

    ${
      includeEvidenceDetail
        ? `
          <section class="evidence-section">
            <div class="evidence-title">Drive Evidence Summary</div>
            <div>${escapeHtml(evidenceSummaryLine)}</div>
            ${driveEvidenceDetailHtml(driveEvidenceSummary)}
          </section>
        `
        : ""
    }

    <footer class="footer">
      RodgersPC internal documentation export. Verify before customer use.
    </footer>
  </main>
</body>
</html>
    `.trim();
  };

  const downloadBlob = (content: string, mimeType: string, ext: string) => {
    const blob = new Blob([content], { type: `${mimeType};charset=utf-8` });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = `${safeFileName}.${ext}`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const handleDownloadFile = (type: ExportExt) => {
    if (type === "txt") {
      downloadBlob(generateText(), "text/plain", "txt");
      return;
    }

    if (type === "md") {
      downloadBlob(generateMarkdown(), "text/markdown", "md");
      return;
    }

    if (type === "doc") {
      downloadBlob(generateRichHtml("clean"), "application/msword", "doc");
      return;
    }

    if (type === "html") {
      downloadBlob(generateRichHtml("clean"), "text/html", "html");
      return;
    }

    downloadBlob(
      JSON.stringify(
        {
          exportedAt: new Date().toISOString(),
          operator: OPERATOR_EMAIL,
          mode: currentMode,
          fileName: safeFileName,
          visualPayloadPresent,
          userVisualPayloadPresent,
          driveVisualPayloadCount,
          driveTextFileCount,
          driveEvidenceSummary,
          driveEvidenceDetail: driveEvidenceDetailLines(driveEvidenceSummary),
          failedDriveLoads: getDriveLoadFailures(driveEvidenceSummary),
          messages,
        },
        null,
        2,
      ),
      "application/json",
      "json",
    );
  };

  const waitForPrintWindowImages = (targetWindow: Window, timeoutMs = 6500): Promise<void> => {
    return new Promise((resolve) => {
      const startedAt = Date.now();

      const checkReady = () => {
        const doc = targetWindow.document;
        const images = Array.from(doc.querySelectorAll("img[data-export-asset='image']")) as HTMLImageElement[];

        if (images.length === 0) {
          resolve();
          return;
        }

        const allLoaded = images.every((img) => img.complete && img.naturalWidth > 0);

        if (allLoaded || Date.now() - startedAt >= timeoutMs) {
          resolve();
          return;
        }

        targetWindow.setTimeout(checkReady, 120);
      };

      checkReady();
    });
  };

  const handlePrintPDF = () => {
    const html = generateRichHtml("clean");
    const printWindow = window.open("", "_blank");

    if (!printWindow) {
      setErrorMessage("Browser blocked the PDF print window. Allow pop-ups for this site and try again.");
      return;
    }

    const pdfName = `${safeFileName}.pdf`;

    try {
      void navigator.clipboard?.writeText(pdfName);
      setErrorMessage(`PDF filename copied: ${pdfName}`);
    } catch {
      setErrorMessage(`PDF filename: ${pdfName}`);
    }

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();

    try {
      printWindow.document.title = safeFileName;
    } catch {
      // Browser title write can fail in some print windows.
    }

    const runPrint = async () => {
      try {
        await waitForPrintWindowImages(printWindow);
        printWindow.focus();
        printWindow.print();
      } catch (error) {
        console.error("PDF print failed:", error);
        setErrorMessage("PDF print failed. Use HTML or Word export, then print to PDF from the browser.");
      }
    };

    printWindow.onload = () => window.setTimeout(() => void runPrint(), 450);
    window.setTimeout(() => void runPrint(), 1400);
  };

  const handleSaveToDrive = async (ext: "txt" | "md" | "doc" | "html") => {
    if (!driveSyncEnabled) {
      setSaveStatus("error");
      setErrorMessage("Drive Sync is off. Turn Drive Sync on before saving exports to Google Drive.");
      return;
    }

    setLastSavedExt(ext);
    setSaveStatus("saving");
    setErrorMessage("");

    let content = "";
    let mimeType = "text/plain";

    if (ext === "txt") {
      content = generateText();
      mimeType = "text/plain";
    } else if (ext === "md") {
      content = generateMarkdown();
      mimeType = "text/markdown";
    } else if (ext === "html") {
      content = generateRichHtml("evidence");
      mimeType = "text/html";
    } else {
      content = generateRichHtml("evidence");
      mimeType = "application/msword";
    }

    try {
      await onUploadFile(`${safeFileName}.${ext}`, content, mimeType);
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), 8000);
    } catch (error) {
      console.error("Drive export failed:", error);
      setSaveStatus("error");
      setErrorMessage(error instanceof Error ? error.message : "Drive export failed. No fake Drive upload was created.");
      setTimeout(() => setSaveStatus("idle"), 8000);
    }
  };

  const handleExportPNG = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const width = 1100;
    const marginX = 58;
    const contentWidth = width - marginX * 2;
    const lineHeight = 18;
    const maxCanvasHeight = 24000;

    const wrapText = (text: string, maxWidth: number, font: string): string[] => {
      ctx.font = font;
      const source = String(text || "").replace(/\r/g, "");
      const output: string[] = [];

      source.split("\n").forEach((paragraph) => {
        if (!paragraph.trim()) {
          output.push("");
          return;
        }

        const words = paragraph.split(/\s+/).filter(Boolean);
        let line = "";

        words.forEach((word) => {
          const test = line ? `${line} ${word}` : word;
          if (ctx.measureText(test).width > maxWidth && line) {
            output.push(line);
            line = word;
          } else {
            line = test;
          }
        });

        if (line) output.push(line);
      });

      return output;
    };

    const entries = await Promise.all(messages.map(async (msg, index) => {
      const sender = msg.role === "user" ? userSenderLabel() : aiSenderLabelForMode(currentMode);
      const body = messageTextForExport(msg);
      const lines = wrapText(body, contentWidth, "14px Arial, sans-serif");
      const assets = collectMessageAssets(msg);
      const drawableAssets = assets.filter((asset) => asset.mimeType.startsWith("image/")).slice(0, 2);
      const loadedImages = (await Promise.all(
        drawableAssets.map(async (asset) => ({ asset, image: await loadExportImage(asset.src) })),
      )).filter((item) => item.image);
      const estimatedImageHeight = loadedImages.length ? loadedImages.length * 230 + 18 : 0;
      const estimatedHeight = 42 + Math.max(1, lines.length) * lineHeight + estimatedImageHeight + (assets.length ? 24 : 0) + 28;
      return { msg, index, sender, lines, assets, loadedImages, estimatedHeight };
    }));

    let requiredHeight = 210 + entries.reduce((sum, entry) => sum + entry.estimatedHeight, 0) + 120;
    const capped = requiredHeight > maxCanvasHeight;
    if (capped) requiredHeight = maxCanvasHeight;

    canvas.width = width;
    canvas.height = Math.max(1100, requiredHeight);

    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.fillStyle = "#071a3d";
    ctx.fillRect(0, 0, canvas.width, 170);

    ctx.fillStyle = "#f6a400";
    ctx.font = "bold 25px Consolas, monospace";
    ctx.fillText("RODGERSPC COMMAND CENTER EXPORT", marginX, 56);

    ctx.fillStyle = "#ffffff";
    ctx.font = "13px Consolas, monospace";
    ctx.fillText(`Operator: ${OPERATOR_EMAIL}`, marginX, 92);
    ctx.fillText(`Mode: ${activeModeLabel}`, marginX, 116);
    ctx.fillText(`Generated: ${generatedDate}`, marginX, 140);

    ctx.fillStyle = "#f8fafc";
    ctx.fillRect(marginX, 188, contentWidth, 74);
    ctx.strokeStyle = "#d7dee8";
    ctx.lineWidth = 1;
    ctx.strokeRect(marginX, 188, contentWidth, 74);

    ctx.fillStyle = "#143f8f";
    ctx.font = "bold 11px Consolas, monospace";
    ctx.fillText("CUSTOMER / FILE", marginX + 14, 210);
    ctx.fillText("SUBJECT", marginX + 360, 210);
    ctx.fillText("MESSAGES", marginX + 760, 210);

    ctx.fillStyle = "#071a3d";
    ctx.font = "bold 14px Arial, sans-serif";
    ctx.fillText((customerName || "RodgersPC").slice(0, 34), marginX + 14, 236);
    ctx.fillText((logTopic || "Command Center Notes").slice(0, 42), marginX + 360, 236);
    ctx.fillText(String(messages.length), marginX + 760, 236);

    let yOffset = 300;

    for (const entry of entries) {
      if (yOffset > canvas.height - 110) break;

      const isUser = entry.msg.role === "user";
      const bubbleX = isUser ? marginX : marginX + 28;
      const bubbleW = contentWidth - 28;
      const remaining = canvas.height - yOffset - 120;
      const bubbleH = Math.min(entry.estimatedHeight, Math.max(90, remaining));

      ctx.fillStyle = isUser ? "#edf6ff" : "#fff7d8";
      ctx.strokeStyle = isUser ? "#9bc7ff" : "#f6c453";
      ctx.lineWidth = 2;
      ctx.fillRect(bubbleX, yOffset, bubbleW, bubbleH);
      ctx.strokeRect(bubbleX, yOffset, bubbleW, bubbleH);

      ctx.fillStyle = isUser ? "#143f8f" : "#f6a400";
      ctx.fillRect(bubbleX, yOffset, 10, bubbleH);

      ctx.fillStyle = isUser ? "#143f8f" : "#8a5a00";
      ctx.font = "bold 17px Consolas, monospace";
      ctx.fillText(`${entry.index + 1}. ${entry.sender} — ${entry.msg.timestamp}`, bubbleX + 22, yOffset + 30);

      ctx.fillStyle = "#0b1329";
      ctx.font = "14px Arial, sans-serif";
      let lineY = yOffset + 56;
      const maxLines = Math.max(1, Math.floor((bubbleH - 86) / lineHeight));
      const visibleLines = entry.lines.slice(0, maxLines);

      visibleLines.forEach((line) => {
        ctx.fillText(line, bubbleX + 22, lineY);
        lineY += lineHeight;
      });

      if (entry.lines.length > visibleLines.length) {
        ctx.fillStyle = "#9a3412";
        ctx.font = "bold 12px Consolas, monospace";
        ctx.fillText(`[PNG capped: ${entry.lines.length - visibleLines.length} more lines not shown. Use TXT/MD/JSON/DOC for complete record.]`, bubbleX + 22, lineY + 8);
      }

      if (entry.loadedImages.length > 0) {
        const maxImageW = Math.min(460, bubbleW - 44);
        const maxImageH = 210;

        for (const loaded of entry.loadedImages) {
          const image = loaded.image as HTMLImageElement;
          if (!image || lineY > yOffset + bubbleH - 245) break;

          const sourceW = image.naturalWidth || image.width || maxImageW;
          const sourceH = image.naturalHeight || image.height || maxImageH;
          const scale = Math.min(maxImageW / sourceW, maxImageH / sourceH, 1);
          const drawW = Math.max(1, Math.round(sourceW * scale));
          const drawH = Math.max(1, Math.round(sourceH * scale));
          const drawX = bubbleX + 22;
          const drawY = lineY + 8;

          ctx.fillStyle = "#ffffff";
          ctx.fillRect(drawX - 4, drawY - 4, drawW + 8, drawH + 8);
          ctx.strokeStyle = "#d8dee8";
          ctx.lineWidth = 1;
          ctx.strokeRect(drawX - 4, drawY - 4, drawW + 8, drawH + 8);
          ctx.drawImage(image, drawX, drawY, drawW, drawH);
          lineY = drawY + drawH + 18;
        }
      }

      if (entry.assets.length > 0 && lineY < yOffset + bubbleH - 18) {
        ctx.fillStyle = "#8a5a00";
        ctx.font = "bold 11px Consolas, monospace";
        ctx.fillText(`[${entry.assets.length} visual attachment(s) retained in export]`, bubbleX + 22, yOffset + bubbleH - 18);
      }

      yOffset += bubbleH + 22;
    }

    if (capped) {
      ctx.fillStyle = "#fff7ed";
      ctx.fillRect(marginX, canvas.height - 86, contentWidth, 34);
      ctx.fillStyle = "#9a3412";
      ctx.font = "bold 12px Consolas, monospace";
      ctx.fillText("PNG reached browser canvas safety limit. Use TXT, MD, JSON, DOC, or HTML for the complete transcript.", marginX + 12, canvas.height - 64);
    }

    ctx.fillStyle = "#071a3d";
    ctx.fillRect(0, canvas.height - 44, canvas.width, 44);
    ctx.fillStyle = "#ffffff";
    ctx.font = "11px Consolas, monospace";
    ctx.fillText("RodgersPC internal export image. Verify before using as a customer report.", marginX, canvas.height - 18);

    const link = document.createElement("a");
    link.download = `${safeFileName}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  const modalBgClassName = isLight
    ? "bg-white border-slate-300 text-slate-900"
    : isDark
      ? "bg-[#151515] border-neutral-800 text-neutral-100"
      : "bg-[#051126] border-[#f6a400]/45 text-slate-100";

  const headerBgClassName = isLight
    ? "bg-slate-50 border-b border-slate-200"
    : isDark
      ? "bg-[#0f0f0f] border-b border-neutral-800"
      : "bg-[#020b1e]/90 border-b border-[#112240]";

  const tabBgClassName = isLight
    ? "bg-slate-100/50 border-b border-slate-200"
    : isDark
      ? "bg-[#0f0f0f]/40 border-b border-neutral-800"
      : "bg-[#020b1e]/50 border-b border-[#112240]";

  const inputBgClassName = isLight
    ? "bg-slate-50 border-slate-200 text-slate-900 focus:border-amber-500/75"
    : isDark
      ? "bg-[#1f1f1f] border-neutral-700 text-neutral-100 focus:border-teal-500/65"
      : "bg-[#020b1e] border-[#112240] text-slate-200 focus:border-[#f6a400]/65";

  const cardContainerBgClassName = isLight
    ? "bg-slate-50/80 border-slate-200"
    : isDark
      ? "bg-[#1c1c1c] border-neutral-800"
      : "bg-[#020b1e]/80 border-[#f6a400]/30";

  const buttonBgClassName = isLight
    ? "bg-slate-50 hover:bg-slate-100 border-slate-200 text-slate-700 hover:text-slate-900"
    : isDark
      ? "bg-[#1f1f1f] hover:bg-[#282828] border-neutral-800 text-neutral-300 hover:text-white"
      : "bg-[#020b1e]/90 hover:bg-[#f6a400]/10 border-[#112240] hover:border-[#f6a400]/35 text-slate-300";

  const footerBgClassName = isLight
    ? "bg-slate-50 border-t border-slate-200"
    : isDark
      ? "bg-[#0f0f0f] border-t border-neutral-800"
      : "bg-[#020b1e] border-t border-[#112240]";

  const labelColorClassName = isLight
    ? "text-slate-500 font-bold"
    : isDark
      ? "text-neutral-400 font-bold"
      : "text-slate-400 font-extrabold";

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm">
      <canvas ref={canvasRef} className="hidden" />

      <div className={`relative w-full max-w-[560px] border rounded-2xl shadow-2xl overflow-hidden font-sans ${modalBgClassName}`}>
        {!isLight && (
          <div className="absolute top-0 left-1/2 -translate-x-1/2 h-[3px] w-56 bg-gradient-to-r from-transparent via-[#f6a400] to-transparent" />
        )}

        <div className={`p-4 flex items-center justify-between ${headerBgClassName}`}>
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#0b2f73] via-[#143f8f] to-[#f6a400] flex items-center justify-center shadow-lg">
              <CloudLightning className="w-4.5 h-4.5 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-1.5">
                <span className="font-extrabold text-sm tracking-wider uppercase font-display">Export Chat</span>
                <span className="text-[9px] font-black tracking-widest bg-amber-500/10 text-amber-500 px-1.5 py-0.5 rounded border border-amber-500/20 uppercase">
                  RodgersPC
                </span>
              </div>
              <p className={`text-[9.5px] font-mono ${isLight ? "text-slate-500" : "text-slate-400"}`}>
                Clean local export with real image preservation
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div
              className={`px-2 h-6 rounded-full border flex items-center justify-center text-[9px] font-mono font-bold ${
                visualPayloadPresent
                  ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-500"
                  : isLight
                    ? "bg-slate-200 border-slate-300 text-slate-600"
                    : "bg-neutral-800 border-neutral-700 text-neutral-400"
              }`}
              title="Visual payload status"
            >
              {visualPayloadPresent ? "VISUAL" : "TEXT"}
            </div>
            <button
              onClick={onClose}
              className={`p-1 rounded-lg transition-all cursor-pointer ${
                isLight ? "hover:bg-slate-200 text-slate-500 hover:text-slate-900" : "hover:bg-[#112240] text-slate-400 hover:text-white"
              }`}
              title="Close exporter"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className={`px-4 py-2 flex items-center justify-between ${tabBgClassName}`}>
          <div className="flex items-center gap-2.5">
            <div
              className={`p-1.5 rounded-md border ${
                isLight ? "bg-slate-200 border-slate-300 text-slate-700" : isDark ? "bg-neutral-800 border-neutral-700 text-neutral-300" : "bg-[#112240] border-[#f6a400]/30 text-[#f6a400]"
              }`}
              title="Transcript export"
            >
              <Terminal className="w-3.5 h-3.5" />
            </div>
            <div className={`p-1.5 rounded-md ${visualPayloadPresent ? "text-emerald-500" : "text-slate-400"}`} title="Images retained in rich exports">
              <ImageIcon className="w-3.5 h-3.5" />
            </div>
            <div className={`p-1.5 rounded-md ${driveSyncEnabled ? "text-cyan-500" : "text-slate-400"}`} title="Drive handoff">
              <Share className="w-3.5 h-3.5" />
            </div>
            <div className={`p-1.5 rounded-md ${isLight ? "text-slate-400" : "text-slate-400"}`} title="Structured output">
              <Sparkles className="w-3.5 h-3.5" />
            </div>
          </div>

          <span className={`text-[9.5px] font-mono ${isLight ? "text-slate-500" : "text-slate-400"}`}>
            {messages.length} messages
          </span>
        </div>

        <div className="p-4 space-y-4 max-h-[74vh] overflow-y-auto">
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <label className={`block text-[9px] font-mono uppercase tracking-wider ${labelColorClassName}`}>
                  Customer Name
                </label>
                <input
                  type="text"
                  value={customerName}
                  onChange={(e) => {
                    setIsManualEdit(false);
                    setCustomerName(e.target.value);
                  }}
                  placeholder="Customer or Multiple Jobs"
                  className={`w-full border rounded-lg px-2.5 py-1.5 text-xs font-mono outline-none font-bold transition-all ${inputBgClassName}`}
                />
              </div>

              <div className="space-y-1">
                <label className={`block text-[9px] font-mono uppercase tracking-wider ${labelColorClassName}`}>
                  Device / Issue
                </label>
                <input
                  type="text"
                  value={logTopic}
                  onChange={(e) => {
                    setIsManualEdit(false);
                    setLogTopic(e.target.value);
                  }}
                  placeholder="Device or issue"
                  className={`w-full border rounded-lg px-2.5 py-1.5 text-xs font-mono outline-none font-bold transition-all ${inputBgClassName}`}
                />
              </div>
            </div>

            <div className="space-y-1 pt-1">
              <label className={`block text-[9px] font-mono uppercase tracking-wider ${labelColorClassName}`}>
                File Name
              </label>

              <div className="flex gap-2">
                <input
                  type="text"
                  value={fileName}
                  onChange={(e) => {
                    setIsManualEdit(true);
                    setFileName(e.target.value);
                  }}
                  placeholder="Customer Name – Device or Issue – MM-DD-YYYY"
                  className={`flex-1 border rounded-lg px-3 py-1.5 text-xs font-mono outline-none font-bold transition-all ${inputBgClassName}`}
                />

                <span
                  className={`px-2.5 py-1.5 text-[9px] rounded-lg font-mono border flex items-center justify-center ${
                    isLight ? "bg-slate-200 border-slate-300 text-slate-600" : "bg-[#112240]/60 border-neutral-800 text-slate-400"
                  }`}
                >
                  .*
                </span>
              </div>

              <p className="text-[8px] text-slate-500 font-mono">
                {isBlogEngineMode(currentMode)
                  ? "Blog format: YYYY-MM-DD – Blog ### – Blog Title."
                  : "Required format: Customer Name – Device or Issue – MM-DD-YYYY."}
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between items-center pb-1">
              <span className={`text-[10px] font-mono uppercase tracking-wider ${labelColorClassName}`}>
                Local Exports
              </span>
              <span className="text-[8.5px] font-mono text-emerald-500 font-bold bg-emerald-500/10 px-1.5 py-0.5 rounded">
                Clean Local
              </span>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={handlePrintPDF}
                className={`flex flex-col items-center justify-center p-3 rounded-xl border transition-all text-center space-y-1.5 group cursor-pointer ${buttonBgClassName}`}
              >
                <div className="w-8 h-8 rounded-lg bg-rose-500/10 border border-rose-500/20 flex items-center justify-center group-hover:scale-105 transition-transform">
                  <Printer className="w-4 h-4 text-rose-500" />
                </div>
                <span className="text-[10px] font-mono font-black">PDF</span>
                <span className={`text-[7.5px] font-mono ${isLight ? "text-slate-400" : "text-slate-500"}`}>Clean print</span>
              </button>

              <button
                type="button"
                onClick={() => handleDownloadFile("doc")}
                className={`flex flex-col items-center justify-center p-3 rounded-xl border transition-all text-center space-y-1.5 group cursor-pointer ${buttonBgClassName}`}
              >
                <div className="w-8 h-8 rounded-lg bg-sky-500/10 border border-sky-500/20 flex items-center justify-center group-hover:scale-105 transition-transform">
                  <MonitorCheck className="w-4 h-4 text-sky-500" />
                </div>
                <span className="text-[10px] font-mono font-black">Word</span>
                <span className={`text-[7.5px] font-mono ${isLight ? "text-slate-400" : "text-slate-500"}`}>.doc images</span>
              </button>

              <button
                type="button"
                onClick={() => handleDownloadFile("html")}
                className={`flex flex-col items-center justify-center p-3 rounded-xl border transition-all text-center space-y-1.5 group cursor-pointer ${buttonBgClassName}`}
              >
                <div className="w-8 h-8 rounded-lg bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center group-hover:scale-105 transition-transform">
                  <Download className="w-4 h-4 text-cyan-500" />
                </div>
                <span className="text-[10px] font-mono font-black">HTML</span>
                <span className={`text-[7.5px] font-mono ${isLight ? "text-slate-400" : "text-slate-500"}`}>Rich</span>
              </button>

              <button
                type="button"
                onClick={() => handleDownloadFile("md")}
                className={`flex flex-col items-center justify-center p-3 rounded-xl border transition-all text-center space-y-1.5 group cursor-pointer ${buttonBgClassName}`}
              >
                <div className="w-8 h-8 rounded-lg bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center group-hover:scale-105 transition-transform">
                  <FileText className="w-4 h-4 text-indigo-500" />
                </div>
                <span className="text-[10px] font-mono font-black">Markdown</span>
                <span className={`text-[7.5px] font-mono ${isLight ? "text-slate-400" : "text-slate-500"}`}>.md</span>
              </button>

              <button
                type="button"
                onClick={() => handleDownloadFile("txt")}
                className={`flex flex-col items-center justify-center p-3 rounded-xl border transition-all text-center space-y-1.5 group cursor-pointer ${buttonBgClassName}`}
              >
                <div className="w-8 h-8 rounded-lg bg-orange-500/10 border border-orange-500/20 flex items-center justify-center group-hover:scale-105 transition-transform">
                  <FileText className="w-4 h-4 text-orange-500" />
                </div>
                <span className="text-[10px] font-mono font-black">Text</span>
                <span className={`text-[7.5px] font-mono ${isLight ? "text-slate-400" : "text-slate-500"}`}>.txt</span>
              </button>

              <button
                type="button"
                onClick={handleExportPNG}
                className={`flex flex-col items-center justify-center p-3 rounded-xl border transition-all text-center space-y-1.5 group cursor-pointer ${buttonBgClassName}`}
              >
                <div className="w-8 h-8 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center group-hover:scale-105 transition-transform">
                  <ImageIcon className="w-4 h-4 text-emerald-500" />
                </div>
                <span className="text-[10px] font-mono font-black">PNG</span>
                <span className={`text-[7.5px] font-mono ${isLight ? "text-slate-400" : "text-slate-500"}`}>Snapshot</span>
              </button>

              <button
                type="button"
                onClick={() => handleDownloadFile("json")}
                className={`flex flex-col items-center justify-center p-3 rounded-xl border transition-all text-center space-y-1.5 group cursor-pointer ${buttonBgClassName}`}
              >
                <div className="w-8 h-8 rounded-lg bg-rose-500/10 border border-rose-500/20 flex items-center justify-center group-hover:scale-105 transition-transform">
                  <Terminal className="w-4 h-4 text-rose-500" />
                </div>
                <span className="text-[10px] font-mono font-black">JSON</span>
                <span className={`text-[7.5px] font-mono ${isLight ? "text-slate-400" : "text-slate-500"}`}>Raw</span>
              </button>
            </div>
          </div>

          <div className="pt-2">
            <div className={`border rounded-xl p-3.5 space-y-2 transition-all ${cardContainerBgClassName}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-[10px] font-mono font-black uppercase text-amber-500">
                  <Database className="w-3.5 h-3.5" />
                  <span>Google Drive Export</span>
                </div>

                <span
                  className={`text-[8.5px] font-mono uppercase bg-black/20 px-1.5 py-0.5 rounded ${
                    driveSyncEnabled ? "text-emerald-500 font-bold" : isLight ? "text-slate-400" : "text-slate-500"
                  }`}
                >
                  {driveSyncEnabled ? "Connected" : "Off"}
                </span>
              </div>

              <p className={`text-[9.5px] leading-normal ${isLight ? "text-slate-500" : "text-slate-400"}`}>
                Saves through the real Drive upload path. If Drive upload fails, the app must show an error instead of creating a fake local Drive file.
              </p>

              <div className="grid grid-cols-4 gap-1.5 pt-1.5">
                <button
                  type="button"
                  onClick={() => handleSaveToDrive("doc")}
                  disabled={!driveSyncEnabled || saveStatus === "saving"}
                  className="py-1 px-2 rounded-lg border border-sky-500/30 bg-sky-500/5 hover:bg-sky-500/15 text-sky-500 text-[9px] font-mono font-bold transition-all disabled:opacity-40 disabled:cursor-not-allowed uppercase cursor-pointer"
                >
                  Save .doc
                </button>

                <button
                  type="button"
                  onClick={() => handleSaveToDrive("html")}
                  disabled={!driveSyncEnabled || saveStatus === "saving"}
                  className="py-1 px-2 rounded-lg border border-cyan-500/30 bg-cyan-500/5 hover:bg-cyan-500/15 text-cyan-500 text-[9px] font-mono font-bold transition-all disabled:opacity-40 disabled:cursor-not-allowed uppercase cursor-pointer"
                >
                  Save .html
                </button>

                <button
                  type="button"
                  onClick={() => handleSaveToDrive("md")}
                  disabled={!driveSyncEnabled || saveStatus === "saving"}
                  className="py-1 px-2 rounded-lg border border-indigo-500/30 bg-indigo-500/5 hover:bg-indigo-500/15 text-indigo-500 text-[9px] font-mono font-bold transition-all disabled:opacity-40 disabled:cursor-not-allowed uppercase cursor-pointer"
                >
                  Save .md
                </button>

                <button
                  type="button"
                  onClick={() => handleSaveToDrive("txt")}
                  disabled={!driveSyncEnabled || saveStatus === "saving"}
                  className="py-1 px-2 rounded-lg border border-orange-500/30 bg-orange-500/5 hover:bg-orange-500/15 text-orange-500 text-[9px] font-mono font-bold transition-all disabled:opacity-40 disabled:cursor-not-allowed uppercase cursor-pointer"
                >
                  Save .txt
                </button>
              </div>

              {saveStatus === "saving" && (
                <div className="text-[9.5px] font-mono text-amber-500 animate-pulse text-center pt-1">
                  Saving export to Google Drive...
                </div>
              )}

              {saveStatus === "saved" && (
                <div
                  className={`text-[10px] font-mono p-2.5 rounded-lg border text-left mt-2 space-y-1 ${
                    isLight ? "bg-emerald-50 border-emerald-200 text-emerald-950" : "bg-emerald-950/20 border-emerald-500/35 text-emerald-400"
                  }`}
                >
                  <div className="font-bold flex items-center gap-1.5 border-b pb-1 mb-1 border-emerald-500/25">
                    <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                    <span>Drive save confirmed</span>
                  </div>
                  <div>
                    <span className="opacity-60 uppercase text-[8px] font-bold">File:</span> {safeFileName}.{lastSavedExt}
                  </div>
                  <div>
                    <span className="opacity-60 uppercase text-[8px] font-bold">Result:</span> Uploaded through active Drive session.
                  </div>
                </div>
              )}

              {saveStatus === "error" && (
                <div className="flex gap-2 items-start text-[9.5px] font-mono text-rose-500 font-bold pt-1">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  <span>{errorMessage || "Drive export failed."}</span>
                </div>
              )}
            </div>
          </div>

          {errorMessage && saveStatus !== "error" && (
            <div className="flex gap-2 items-start text-[9.5px] font-mono text-rose-500 font-bold bg-rose-500/5 border border-rose-500/20 rounded-xl p-2.5">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <span>{errorMessage}</span>
            </div>
          )}
        </div>

        <div className={`p-3 text-center ${footerBgClassName}`}>
          <p className="text-[8.5px] font-mono text-slate-500 uppercase tracking-tight">
            Export records existing chat content only. Verify before giving to customer.
          </p>
        </div>
      </div>
    </div>
  );
}
